import {
    CSSProperties,
    memo,
    ReactElement,
    ReactNode,
    SyntheticEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import { InfoCircleOutlined } from '@ant-design/icons'
import { Table, TableProps as AntTableProps, Tooltip } from 'antd'
import {
    ColumnGroupType,
    ColumnType,
    FilterValue,
    TablePaginationConfig,
    type SorterResult,
} from 'antd/es/table/interface'
import classNames from 'classnames'
import produce from 'immer'
import flow from 'lodash/fp/flow'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import isUndefined from 'lodash/isUndefined'
import noop from 'lodash/noop'
import set from 'lodash/set'
import type { GetRowKey } from 'rc-table/es/interface'
import { useTranslation } from 'react-i18next'
import { ResizeCallbackData } from 'react-resizable'

import { Flex } from 'components/core'
import {
    DEFAULT_TABLE_PAGE_SIZE,
    SIMPLE_PAGE_TABLE_SCROLL_REDUCER,
} from 'const/tables'
import { useOrgContext } from 'context/OrgContext'
import { usePageContext } from 'context/PageContext'
import {
    DatadogActionName,
    useMonitorLoadTime,
} from 'features/Monitoring/Datadog'
import { getDateRangeArray, getFormattedDateRange } from 'helpers/dateRange'
import { formatNumber } from 'helpers/formatting'
import { formatPeriodDeltaDateRange } from 'helpers/params'
import { isBoolean, isDefined } from 'helpers/typeGuard'
import { useDeepCompareCallback } from 'hooks'
import useElementSize from 'hooks/useElementSize'
import { theme } from 'styles/theme'
import {
    Field,
    Pagination,
    Path,
    PeriodDeltaType,
    PresetRange,
    Sorter,
} from 'types'

import EmptyTableStateProvider from '../EmptyTableStateProvider/EmptyTableStateProvider'

import { ToggleMetricDeltasCell, ToggleMetricDeltasTitle } from './Columns'
import { TableField } from './localTypes'
import * as styles from './styles.scss'
import { BodyCell, HeaderCell, RowWrapper } from './TableComponents'

interface Widths {
    [id: string]: {
        width?: number
        children?: (number | undefined)[]
    }
}

export interface PaginatedTableProps<RecordType extends object>
    extends Omit<AntTableProps<RecordType>, 'title'> {
    sorter: Sorter
    readonly?: boolean
    shortFormat?: boolean
    showPeriodDeltas?: boolean
    showTotalRow?: boolean
    shouldMonitorTime?: boolean
    hasUpperContent?: boolean

    updateColumns: (columns: Field<RecordType>[]) => void
    updateSorter: (sorter: Sorter) => void
    updatePagination?: (pagination: Pagination) => void
    onViewChanged?: (options?: { noCount?: boolean }) => void
    reloadData: (options?: { noCount?: boolean }) => void
    updateRecord: (data: { rowIndex: number; record: RecordType }) => void
    onHeaderHeightChange?: (height: number) => void

    periodDeltaType?: PeriodDeltaType
    filterDates?: string[] | PresetRange
    periodDeltaDateRange?: string[]
    disablePriorPeriod?: boolean
    priorPeriodWarningMessage?: string
    rangeLag?: number
    // The windows the server reported it queried, for a data source whose rows
    // are stored at a grain coarser than the picked dates can express. Absent —
    // every daily source — means the picked dates are the queried dates and the
    // headers derive them exactly as before.
    reportedRange?: string[]
    reportedPriorRange?: string[]

    // Ant Design required overrides
    columns: Field<RecordType>[]
    rowKey: string | GetRowKey<RecordType>
    loading: boolean
    rowClassName?: AntTableProps['rowClassName']

    title?: ReactNode | (() => ReactElement | null)
    titleStyles?: CSSProperties
    reduceScrollSize?: number
    noScroll?: boolean
    customEmptyComponent?: ReactElement
    customLoadingComponent?: ReactElement
}

const isFieldEqual = ({ field }: Sorter, dataIndex?: Path): boolean => {
    if (typeof dataIndex === 'undefined') {
        return false
    }

    const comparableField = typeof field === 'string' ? [field] : field
    const comparableDataIndex =
        typeof dataIndex === 'string' ? [dataIndex] : dataIndex

    return isEqual(comparableField, comparableDataIndex)
}

function PaginatedTable<T extends object>({
    readonly = false,
    shortFormat = false,
    showPeriodDeltas = false,
    showTotalRow = false,
    shouldMonitorTime = true,
    hasUpperContent = false,

    updateColumns,
    updateSorter,
    updatePagination,
    onViewChanged = noop,
    reloadData,
    updateRecord,
    onHeaderHeightChange,

    periodDeltaType,
    filterDates,
    periodDeltaDateRange,
    disablePriorPeriod,
    priorPeriodWarningMessage,
    rangeLag,
    reportedRange,
    reportedPriorRange,

    columns,
    sorter,
    pagination,

    title,
    titleStyles,
    reduceScrollSize = SIMPLE_PAGE_TABLE_SCROLL_REDUCER,
    noScroll = false,
    sortDirections = ['descend', 'ascend'],
    tableLayout = 'fixed',
    locale,
    customEmptyComponent,
    customLoadingComponent,
    rowClassName,
    ...rest
}: PaginatedTableProps<T>): ReactElement {
    const { t } = useTranslation('table')
    const { org } = useOrgContext()
    const fiscalYearStartMonth = org?.fiscal_year_start_month
    const tableRef = useRef<HTMLDivElement>(null)
    const headerRef = useRef<Element | null>(null)

    const { height } = useElementSize(headerRef)
    useEffect(() => {
        if (headerRef.current && onHeaderHeightChange) {
            onHeaderHeightChange(height)
        }
    }, [height, onHeaderHeightChange])

    useLayoutEffect(() => {
        if (tableRef.current) {
            const el = tableRef.current.querySelector('thead')
            if (el) {
                headerRef.current = el
            }
        }
    }, [])

    // this is a hack to prevent weirdness in header column positions when adding columns to a table
    // try to remove with future upgrades of rc-table and antd
    useEffect(() => {
        if (tableRef.current) {
            const tableBody = tableRef.current?.querySelector('.ant-table-body')
            const hasHorizontalScrollbar =
                tableBody?.clientWidth &&
                tableBody?.scrollWidth &&
                tableBody.clientWidth < tableBody.scrollWidth
            if (hasHorizontalScrollbar && tableBody?.scrollLeft !== undefined) {
                // gitter the scroll position left and right while loading will fix the weird column rendering
                tableBody.scrollLeft += 1
                tableBody.scrollLeft -= 1
            }
        }
    }, [columns])

    const [resizing, setResizing] = useState(false)

    const [widths, setWidths] = useState<Widths>(() =>
        columns.reduce(
            (accumulator: Widths, current) =>
                produce(accumulator, (draft) => {
                    draft[current.id] = {
                        // Ensure we always have a stable width baseline so columns don't collapse
                        // (e.g., during loading states / empty dataSource renders).
                        width:
                            current.antTableColumnOptions.width ??
                            current.minWidth,
                        ...(current.childrenFields
                            ? {
                                  children: current.childrenFields.map(
                                      (child) =>
                                          child.antTableColumnOptions.width ??
                                          child.minWidth
                                  ),
                              }
                            : {}),
                    }
                }),
            {}
        )
    )

    const components = useMemo(() => {
        return {
            header: { cell: HeaderCell },
            body: {
                cell: BodyCell,
                row: RowWrapper,
            },
        }
    }, [])

    const handleResize = useCallback(
        (id: string, childIndex?: number, minWidth?: number) =>
            (_e: SyntheticEvent, { size }: ResizeCallbackData) => {
                setResizing(true)
                if (!isUndefined(minWidth) && size.width < minWidth) {
                    return
                }

                const width = isUndefined(minWidth)
                    ? size.width
                    : Math.max(minWidth, size.width)

                setWidths(
                    produce((draft: Widths) => {
                        if (!isUndefined(childIndex)) {
                            set(draft, [id, 'children', childIndex], width)
                        } else {
                            set(draft, [id, 'width'], width)
                        }
                    })
                )
            },
        []
    )

    const handleResizeStop = useDeepCompareCallback(
        (id: string, childIndex?: number) =>
            (_e: SyntheticEvent, { size }: ResizeCallbackData) => {
                setResizing(false)
                updateColumns(
                    produce(columns, (draft) => {
                        const parentIndex = draft.findIndex(
                            (element) => element.id === id
                        )
                        if (childIndex) {
                            set(
                                draft,
                                [
                                    parentIndex,
                                    'childrenFields',
                                    childIndex,
                                    'antTableColumnOptions',
                                    'width',
                                ],
                                size.width
                            )
                        } else {
                            draft[parentIndex].antTableColumnOptions.width =
                                size.width
                        }
                    })
                )
            },
        [columns, updateColumns]
    )

    const areSorterEqual = (a: Sorter, b: Sorter): boolean => {
        if (a.order !== b.order) {
            return false
        }
        const aFields = a.field instanceof Array ? a.field : [a.field]
        const bFields = b.field instanceof Array ? b.field : [b.field]
        return isEqual(aFields, bFields)
    }

    const handleOnChange = useDeepCompareCallback(
        (
            nextPagination: TablePaginationConfig,
            _nextFilters: Record<string, FilterValue | null>,
            nextSorter: SorterResult<T> | SorterResult<T>[]
        ): void => {
            let viewChanged = false

            // Update sorter
            if (
                !(nextSorter instanceof Array) &&
                !areSorterEqual(nextSorter, sorter)
            ) {
                updateSorter({
                    field: nextSorter.field,
                    order: nextSorter.order,
                })
                viewChanged = true
            }

            // Update pagination
            if (
                pagination &&
                updatePagination &&
                (nextPagination.current !== pagination.current ||
                    nextPagination.pageSize !== pagination.pageSize)
            ) {
                updatePagination({
                    current: nextPagination.current,
                    pageSize: nextPagination.pageSize,
                })
                viewChanged = true
            }

            if (viewChanged) {
                onViewChanged({ noCount: true })
            }
        },
        [
            pagination,
            reloadData,
            onViewChanged,
            sorter.field,
            sorter.order,
            updatePagination,
            updateSorter,
        ]
    )

    const filterHiddenColumns = useCallback(
        (cols: TableField<T>[]): TableField<T>[] =>
            cols.filter((col) => col.isVisible || col.isVisible === null),
        []
    )

    const expandWithChildrenColumns = useDeepCompareCallback(
        (cols: TableField<T>[]): TableField<T>[] => {
            if (!showPeriodDeltas) {
                return cols
            }
            return cols.reduce((accumulator: TableField<T>[], current) => {
                const childrenToExpand: TableField<T>[] = []
                const hasChildren = (current.childrenFields?.length ?? 0) > 0

                accumulator.push(current)

                if (hasChildren) {
                    let isCollapsed = true
                    current.childrenFields?.forEach((child, childIndex) => {
                        if (
                            child.isVisible &&
                            (child.metricOptions?.isDelta ||
                                child.metricOptions?.isPriorPeriod)
                        ) {
                            childrenToExpand.push({
                                ...child,
                                id: current.id,
                                childIndex,
                            })
                            isCollapsed = false
                        }
                    })
                    childrenToExpand.unshift({
                        id: 'deltaColumn',
                        isVisible: true,
                        isResizeable: false,
                        minWidth: 40,

                        priorPeriodOptions: {
                            render: ({ record, rowIndex, isTotalCell }) => {
                                return (
                                    <ToggleMetricDeltasCell
                                        parent={current}
                                        record={record}
                                        columns={columns}
                                        rowIndex={rowIndex}
                                        reloadData={reloadData}
                                        readonly={readonly}
                                        shortFormat={shortFormat}
                                        updateColumns={updateColumns}
                                        updateRecord={updateRecord}
                                        isTotalCell={isTotalCell}
                                    />
                                )
                            },
                        },
                        antTableColumnOptions: {
                            title: (
                                <ToggleMetricDeltasTitle
                                    updateColumns={updateColumns}
                                    columns={columns}
                                    parentId={current.id}
                                    isCollapsed={isCollapsed}
                                />
                            ),
                            width: 40,
                        },
                    })
                }

                return [...accumulator, ...childrenToExpand]
            }, [])
        },
        [
            columns,
            readonly,
            reloadData,
            shortFormat,
            showPeriodDeltas,
            updateColumns,
            updateRecord,
        ]
    )

    const includeExtraPropsForCustomComponents = useDeepCompareCallback(
        (cols: TableField<T>[]): TableField<T>[] =>
            cols.map((col, idx) =>
                produce(col, (draft) => {
                    draft.antTableColumnOptions.onHeaderCell = ({ width }) => {
                        return {
                            isResizeable: col.isResizeable,
                            width,
                            onResize: handleResize(
                                col.id,
                                col.childIndex,
                                col.minWidth
                            ),
                            onResizeStop: handleResizeStop(
                                col.id,
                                col.childIndex
                            ),
                        }
                    }
                    draft.antTableColumnOptions.onCell = (
                        record,
                        rowIndex
                    ) => ({
                        columns,
                        value: col.dataIndex
                            ? get(record, col.dataIndex)
                            : null,
                        record,
                        rowIndex,
                        renderOptions: col.renderOptions,
                        metricOptions: col.metricOptions,
                        priorPeriodOptions: col.priorPeriodOptions,
                        isTotalSupported: col.isTotalSupported,
                        reloadData,
                        readonly:
                            readonly || !!col.antTableColumnOptions.readonly, // the cell is readonly if the whole table is readonly or the given column is
                        shortFormat,
                        updateColumns,
                        updateRecord,
                        colIndex: idx,
                    })
                })
            ),
        [
            columns,
            handleResize,
            handleResizeStop,
            readonly,
            reloadData,
            shortFormat,
            updateColumns,
            updateRecord,
        ]
    )

    const includeColumnSortOrder = useDeepCompareCallback(
        (cols: TableField<T>[]): TableField<T>[] =>
            cols.map((col) => {
                if (isFieldEqual(sorter, col.dataIndex) && sorter.order) {
                    return produce(col, (draft) => {
                        draft.antTableColumnOptions.sortOrder = sorter.order
                    })
                }
                return col
            }),
        [sorter]
    )

    const mergeWithLocalWidths = useDeepCompareCallback(
        (cols: TableField<T>[]): TableField<T>[] => {
            return cols.map((col) =>
                produce(col, (draft) => {
                    if (widths[draft.id]) {
                        let { width: localWidth } = widths[draft.id]
                        const { children } = widths[draft.id]
                        if (!isUndefined(col.childIndex) && children) {
                            localWidth = children[col.childIndex]
                        }
                        const nextWidth =
                            localWidth ??
                            draft.antTableColumnOptions.width ??
                            draft.minWidth
                        draft.antTableColumnOptions.width = Math.max(
                            draft.minWidth,
                            nextWidth ?? 0
                        )
                    } else {
                        // If we don't have a locally-tracked width yet, still force a min width.
                        draft.antTableColumnOptions.width = Math.max(
                            draft.minWidth,
                            draft.antTableColumnOptions.width ?? 0
                        )
                    }
                })
            )
        },
        [widths]
    )

    const convertToAntColumnType = useCallback(
        (
            tableColumns: TableField<T>[]
        ): (ColumnType<T> | ColumnGroupType<T>)[] => {
            const buildPriorPeriodGroupTitle = (
                parentColumn: ColumnType<T>,
                parentId: string
            ): ReactNode => {
                return (
                    <Flex style={{ cursor: 'default' }}>
                        <span style={{ flex: 1 }}>
                            {typeof parentColumn.title === 'function'
                                ? parentColumn.title({})
                                : parentColumn.title}
                        </span>
                        <ToggleMetricDeltasTitle
                            updateColumns={updateColumns}
                            columns={columns}
                            parentId={parentId}
                            isCollapsed={false}
                            showVerticalLine={false}
                        />
                    </Flex>
                )
            }

            const buildCurrentPeriodHeaderTitle = (): ReactNode => {
                const headerLabel = t(
                    'table:fields.currentPeriod.name',
                    'Selected Range'
                )

                if (!filterDates || !periodDeltaType) {
                    return headerLabel
                }

                const range =
                    reportedRange ??
                    getDateRangeArray(
                        filterDates,
                        false,
                        rangeLag,
                        fiscalYearStartMonth
                    )

                return (
                    <Flex direction="column" alignItems="normal">
                        <span>{headerLabel}</span>
                        <Flex
                            gap={4}
                            style={{
                                color: theme.colors.colorTableHeadSubtitle,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: theme.fontSize.bodyXs,
                                    fontWeight: theme.fontWeight.semiBold,
                                    flex: 1,
                                }}
                            >
                                {getFormattedDateRange(
                                    Object.values(range),
                                    undefined,
                                    false,
                                    rangeLag,
                                    fiscalYearStartMonth
                                )}
                            </span>
                            {disablePriorPeriod && (
                                <Tooltip title={priorPeriodWarningMessage}>
                                    <InfoCircleOutlined />
                                </Tooltip>
                            )}
                        </Flex>
                    </Flex>
                )
            }

            const buildPriorPeriodHeaderTitle = (): ReactNode => {
                const headerLabel = t(
                    'table:fields.priorPeriod.name',
                    'Prior Period'
                )

                if (!filterDates || !periodDeltaType) {
                    return headerLabel
                }

                const previousDates =
                    reportedPriorRange ??
                    (periodDeltaType === 'custom' &&
                    periodDeltaDateRange &&
                    periodDeltaDateRange.length > 0
                        ? periodDeltaDateRange
                        : formatPeriodDeltaDateRange(
                              true,
                              periodDeltaType,
                              [],
                              filterDates,
                              'report_date',
                              rangeLag,
                              fiscalYearStartMonth
                          ))

                return (
                    <Flex direction="column" alignItems="normal">
                        <span>{headerLabel}</span>
                        <Flex
                            gap={4}
                            style={{
                                color: theme.colors.colorTableHeadSubtitle,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: theme.fontSize.bodyXs,
                                    fontWeight: theme.fontWeight.semiBold,
                                    flex: 1,
                                }}
                            >
                                {getFormattedDateRange(
                                    Object.values(previousDates),
                                    undefined,
                                    false,
                                    rangeLag,
                                    fiscalYearStartMonth
                                )}
                            </span>
                            {disablePriorPeriod && (
                                <Tooltip title={priorPeriodWarningMessage}>
                                    <InfoCircleOutlined />
                                </Tooltip>
                            )}
                        </Flex>
                    </Flex>
                )
            }

            const buildPriorPeriodColumns = (
                cols: TableField<T>[],
                antCols: Field<T>['antTableColumnOptions'][],
                priorPeriodCols: TableField<T>[]
            ): Field<T>['antTableColumnOptions'][] => {
                const priorPeriodColsGroupById: {
                    [key: string]: TableField<T>[]
                } = priorPeriodCols.reduce((acc, curr) => {
                    acc[curr.id] = acc[curr.id] || []
                    acc[curr.id].push(curr)
                    return acc
                }, Object.create(null))

                let offset = 0

                const adjustedAntCols = [...antCols]

                Object.entries(priorPeriodColsGroupById).forEach(
                    ([id, priorCols]) => {
                        const antPriorPeriodCols = priorCols.map((col) => ({
                            ...col.antTableColumnOptions,
                        }))

                        const parentColumnIndex = cols
                            .filter((col) => col.childIndex == null)
                            .findIndex((col) => col.id === id)

                        const adjustedParentColumnIndex =
                            parentColumnIndex + offset
                        const parentColumn =
                            adjustedAntCols[adjustedParentColumnIndex]

                        const deltaColumnIndex = parentColumnIndex + 1
                        const deltaColumn =
                            adjustedAntCols[deltaColumnIndex + offset]

                        // Add a delta column with no button title
                        antPriorPeriodCols.unshift({
                            ...deltaColumn,
                            title: undefined,
                            width: 20,
                            className: styles['prior-period-column-delta-sm'],
                        })

                        // Add the original column inside the group
                        antPriorPeriodCols.unshift({
                            ...parentColumn,
                            title: buildCurrentPeriodHeaderTitle(),
                            minWidth: 140,
                            width: Math.max(parentColumn.width ?? 0, 140),
                        })

                        // Add prior period column
                        antPriorPeriodCols[2] = {
                            ...antPriorPeriodCols[2],
                            title: buildPriorPeriodHeaderTitle(),
                        }

                        // Add classes so we can adjust the bg color and borders
                        antPriorPeriodCols.forEach((col, index) => {
                            if (index === 0) {
                                col.className = classNames(
                                    col.className,
                                    styles['prior-period-column-first']
                                )
                            } else if (
                                index ===
                                antPriorPeriodCols.length - 1
                            ) {
                                col.className = classNames(
                                    col.className,
                                    styles['prior-period-column-last']
                                )
                            }
                            col.className = classNames(
                                col.className,
                                styles['prior-period-column']
                            )
                        })

                        adjustedAntCols.splice(
                            adjustedParentColumnIndex + 2, // adding after the parent column and delta column
                            0,
                            {
                                title: buildPriorPeriodGroupTitle(
                                    parentColumn,
                                    id
                                ),
                                align: 'center' as const,
                                children: antPriorPeriodCols,
                                className: classNames(
                                    styles['prior-period-column'],
                                    styles['prior-period-column-group'],
                                    styles['prior-period-column-first'],
                                    styles['prior-period-column-last']
                                ),
                            } as Field<T>['antTableColumnOptions']
                        )

                        // remove the original column and the delta column
                        adjustedAntCols.splice(adjustedParentColumnIndex, 2)

                        // we added a column and removed two,
                        // adjust offset tracking index differences
                        // between the original and adjusted columns
                        offset -= 1
                    }
                )
                return adjustedAntCols
            }

            let antCols = tableColumns
                .filter((col) => col.childIndex == null)
                .map((col): Field<T>['antTableColumnOptions'] => ({
                    ...col.antTableColumnOptions,
                }))

            const priorPeriodCols = tableColumns.filter(
                (col) => col.childIndex != null
            )

            if (priorPeriodCols.length > 0) {
                antCols = buildPriorPeriodColumns(
                    tableColumns,
                    antCols,
                    priorPeriodCols
                )
            }

            return antCols
        },
        [
            columns,
            disablePriorPeriod,
            filterDates,
            fiscalYearStartMonth,
            periodDeltaDateRange,
            periodDeltaType,
            priorPeriodWarningMessage,
            rangeLag,
            reportedRange,
            reportedPriorRange,
            t,
            updateColumns,
        ]
    )

    const processColumns = useCallback(
        (columnsToProcess: Field<T>[]) => {
            const fn = flow(
                filterHiddenColumns,
                expandWithChildrenColumns,
                includeExtraPropsForCustomComponents,
                includeColumnSortOrder
            )
            return fn(columnsToProcess)
        },
        [
            filterHiddenColumns,
            expandWithChildrenColumns,
            includeExtraPropsForCustomComponents,
            includeColumnSortOrder,
        ]
    )

    const processedColumns = useMemo(
        () => processColumns(columns),
        [columns, processColumns]
    )

    const mergeAndConvert = useCallback(
        (columnsToMerge: Field<T>[]) => {
            const fn = flow(mergeWithLocalWidths, convertToAntColumnType)
            return fn(columnsToMerge)
        },
        [mergeWithLocalWidths, convertToAntColumnType]
    )

    const tableColumns = useMemo(
        () => mergeAndConvert(processedColumns),
        [mergeAndConvert, processedColumns]
    )

    const leftFixedColumns: typeof tableColumns = []
    const rightFixedColumns: typeof tableColumns = []
    const otherColumns: typeof tableColumns = []

    tableColumns.forEach((column) => {
        if (column.fixed === 'left') {
            leftFixedColumns.push(column)
        } else if (column.fixed === 'right') {
            rightFixedColumns.push(column)
        } else {
            otherColumns.push(column)
        }
    })

    const sortedTableColumns = [
        ...leftFixedColumns,
        ...otherColumns,
        ...rightFixedColumns,
    ]

    useMonitorLoadTime({
        actionName: DatadogActionName.LOAD_TABLE,
        loading: rest.loading,
        shouldMonitorTime,
        tableContext: {
            column_count: tableColumns.length,
            sort_by: {
                field: sorter.field,
                order: sorter.order,
            },
            pagination: {
                ...(isDefined(pagination) && !isBoolean(pagination)
                    ? {
                          pageSize: pagination.pageSize,
                          current: pagination.current,
                          total: pagination.total,
                      }
                    : {}),
            },
        },
    })

    const titleRef = useRef<HTMLDivElement>(null)
    const { height: titleHeight } = useElementSize<HTMLDivElement>(titleRef)

    const {
        headerSize: { height: headerHeight },
    } = usePageContext()
    const scroll = noScroll
        ? ({ x: true } as const)
        : {
              y: `max(calc(100vh - ${headerHeight + titleHeight + reduceScrollSize}px), 400px)`,
          }

    return (
        <div
            ref={tableRef}
            className={`${styles['paginated-table-wrapper']} ${
                hasUpperContent ? styles['has-upper-content'] : ''
            } ${rest.loading ? '' : styles['table-loaded']}`}
        >
            {!!title && (
                <div
                    ref={titleRef}
                    className={titleStyles ? undefined : 'ant-table-title'}
                    style={titleStyles}
                >
                    {typeof title === 'function' ? title() : title}
                </div>
            )}
            <EmptyTableStateProvider
                isLoading={rest.loading}
                customEmptyComponent={customEmptyComponent}
                customLoadingComponent={customLoadingComponent}
            >
                <Table
                    columns={sortedTableColumns}
                    components={components}
                    scroll={scroll}
                    sortDirections={sortDirections}
                    onChange={handleOnChange}
                    locale={locale}
                    rowClassName={rowClassName}
                    pagination={
                        pagination === false
                            ? false
                            : {
                                  size: 'default',
                                  showSizeChanger: true,
                                  position: ['bottomCenter'],
                                  showLessItems: true,
                                  showTotal: (total, range) =>
                                      t(
                                          'table:PaginatedTable.paginationText',
                                          '{{minRange}}-{{maxRange}} of {{total}} Total',
                                          {
                                              minRange: formatNumber(range[0]),
                                              maxRange: formatNumber(range[1]),
                                              total: formatNumber(total),
                                          }
                                      ),
                                  ...pagination,
                                  pageSize:
                                      pagination?.pageSize ??
                                      DEFAULT_TABLE_PAGE_SIZE,
                              }
                    }
                    size="small"
                    showSorterTooltip={false}
                    tableLayout={tableLayout}
                    onRow={(record, rowIndex) =>
                        ({
                            record,
                            rowIndex,
                            resizing,
                            showTotalRow,
                        }) as any
                    }
                    indentSize={16}
                    {...rest}
                    loading={rest.loading && !customLoadingComponent}
                />
            </EmptyTableStateProvider>
        </div>
    )
}

export default memo(PaginatedTable, isEqual) as typeof PaginatedTable
